import React, { useEffect, useRef, useState } from 'react'
import propTypes from 'prop-types'
import Typography from '@material-ui/core/Typography'
import Chip from '@material-ui/core/Chip'
import Tooltip from '@material-ui/core/Tooltip'
import { makeStyles } from '@material-ui/core/styles'

/**
 * Width the description cell always occupies.
 *
 * Fixed rather than content-driven because the discontinued notice is wider than a bare
 * description: letting the column size itself made the whole table slide left and right as the
 * user paged or sorted and the rows carrying a notice came in and out of view. Every cell
 * reserves the same width, so the layout stays put whether or not the notice is there. This is
 * the same fixed-width-wrapper approach the State and Quantity cells of this grid already use.
 */
export const DESCRIPTION_CELL_WIDTH = '210px'

/**
 * Text the discontinued label always carries.
 *
 * A constant rather than an inline literal because the requirement fixes it: the label reads the
 * same on every row, with no variant derived from the part or its substitute.
 */
export const DISCONTINUED_LABEL = 'Obsolete'

/**
 * Whether a spare part row is announced as discontinued.
 *
 * The catalog flag alone is enough. A row flagged without a resolved substitute still has
 * something to tell the user — that the part is no longer supplied — so it carries the label even
 * though there is no replacement to name.
 * @param {*} item
 */
export const hasDiscontinuedNotice = (item) => Boolean(item && item.IsDiscontinued)

/**
 * Whether a discontinued row has a substitute to name beside its label.
 *
 * Split from the notice predicate above because the two questions came apart: a row can be worth
 * announcing while having no replacement registered. Keeping them fused is what used to make such
 * a row render as though the part were current.
 * @param {*} item
 */
export const hasSubstituteLegend = (item) => Boolean(hasDiscontinuedNotice(item) && item.Substitute)

/**
 * The legend naming the material that replaces a discontinued part, or an empty string when the
 * catalog registered none.
 * @param {*} item
 */
export const substituteLegend = (item) => (hasSubstituteLegend(item) ? `Substitute: ${item.Substitute.Code} | ${item.Substitute.Description}` : '')

const useNoticeStyles = makeStyles((theme) => ({
	notice: {
		display: 'flex',
		alignItems: 'center',
		marginTop: '2px',
		// A flex container inherits the same refusal to shrink as its items; without this the row
		// grows to fit the legend instead of letting the legend clip.
		minWidth: 0
	},
	label: {
		height: '18px',
		marginRight: '6px',
		fontSize: '0.65rem',
		fontWeight: 600,
		// Taken from the project palette rather than written here, so a change of theme carries
		// the notice with it. The amber pair lives beside the orange the palette already had.
		backgroundColor: theme.palette.colors.orangeLight,
		color: theme.palette.colors.orangeDark,
		// The label reads in full at every width; the legend beside it is what gives way.
		flexShrink: 0
	},
	legend: {
		// A flex item refuses to shrink below its own content unless told to. Left at the default
		// the un-wrapped legend keeps its full width, crosses the fixed cell and prints over the
		// column beside it instead of ending in an ellipsis.
		minWidth: 0,
		flex: 1,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis'
	}
}))

/**
 * The compact discontinued notice that annotates a spare part row.
 *
 * One line: a label fixing the part as discontinued, and beside it the material that replaces it.
 * A component rather than plain markup because the legend has to measure itself — the tooltip is
 * owed only when the text actually overflows, and CSS cannot report that.
 */
export const DiscontinuedNotice = ({ item }) => {
	const classes = useNoticeStyles()
	const legendRef = useRef(null)
	const [isOverflowing, setIsOverflowing] = useState(false)
	const legend = substituteLegend(item)

	useEffect(() => {
		let cancelled = false
		// scrollWidth is the width the text would need; clientWidth is what the cell granted it.
		const measure = () => {
			if (cancelled) return
			const node = legendRef.current
			setIsOverflowing(Boolean(node) && node.scrollWidth > node.clientWidth)
		}

		measure()

		// Web fonts settle after the first paint and the text metrics move with them, so a legend
		// measured against the fallback face can report that it fits while the real one overflows.
		// Measuring again once the fonts resolve closes that window. The flag matters because the
		// promise can land after the row has been paged away and the component is gone.
		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(measure)
		}

		return () => {
			cancelled = true
		}
		// The cell width is a module constant, so only a change of text can change the verdict.
	}, [legend])

	return (
		<div className={classes.notice}>
			<Chip size='small' label={DISCONTINUED_LABEL} className={classes.label} />
			{legend !== '' && (
				<Tooltip title={isOverflowing ? legend : ''}>
					<Typography variant='caption' component='div' ref={legendRef} className={classes.legend}>
						{legend}
					</Typography>
				</Tooltip>
			)}
		</div>
	)
}

DiscontinuedNotice.propTypes = {
	/** Spare part row the notice annotates; must be flagged as discontinued */
	item: propTypes.object.isRequired
}

/**
 * Render the description cell, with the discontinued notice underneath when there is one.
 *
 * The notice lives here rather than in a column of its own so it reads as an annotation on the
 * part it describes. A current part shows the description by itself, so rows that are not in the
 * substitutable materials catalog read exactly as they did before this capability existed.
 * @param {*} item
 */
export const renderDescriptionCell = (item) => {
	if (!item) return null
	return (
		<div style={{ width: DESCRIPTION_CELL_WIDTH, overflow: 'hidden' }}>
			<Typography variant='body2' component='div'>
				{item.Description}
			</Typography>
			{hasDiscontinuedNotice(item) && <DiscontinuedNotice item={item} />}
		</div>
	)
}

export const dataGridConfiguration = [
	{
		title: 'Code',
		dataSource: 'Code',
		sortable: true
	},
	{
		title: 'Description',
		dataSource: 'Description',
		sortable: true,
		width: DESCRIPTION_CELL_WIDTH,
		onRenderProperty: renderDescriptionCell
	}
	// {
	// 	title: 'Material',
	// 	dataSource: 'Material',
	// 	sortable: true
	// },
	// {
	// 	title: 'Description',
	// 	dataSource: 'DescriptionMat',
	// 	sortable: true
	// },
	// {
	// 	title: 'Quantity SAP',
	// 	dataSource: 'QuantitySAP',
	// 	sortable: true
	// }
]

export default dataGridConfiguration
